import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Switch,
  IconButton,
  Pagination,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/Print";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useTranslation } from "react-i18next";
import JumboSearch from "@jumbo/components/JumboSearch";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import VoucherCodesPrintModal from "./VoucherCodesPrintModal";
import { getDSOKProducts } from "app/services/api/management";
import {
  normalizeDsokProductsList,
  resolveProductDisplayName,
} from "../voucherTicketProducts";

const VOUCHERS_I18N = "pages.dsokDetails.accessSystem.vouchers";

/** JumboSearch persistence key — voucher codes tab (aligned with DSOKProducts). */
const VOUCHER_CODES_SEARCH_PAGE = "AccessSystemVoucherCodes";

/** Max voucher codes per series (generation + persistence). */
const MAX_CODES_PER_SERIES = 200;
const CODES_PAGE_SIZE = 20;

function filterVoucherCodesBySearch(rows, queryRaw, statusLabelForRow) {
  const q = String(queryRaw ?? "")
    .trim()
    .toLowerCase();
  if (!q) return rows;
  return (rows || []).filter((row) => {
    const code = String(row?.code ?? "").toLowerCase();
    const statusLabel = String(statusLabelForRow(row) ?? "").toLowerCase();
    return code.includes(q) || statusLabel.includes(q);
  });
}

/** `inProgress` rows first; stable order within each group (ES stable sort). */
function sortVoucherCodesInProgressFirst(rows) {
  if (!Array.isArray(rows) || rows.length <= 1) {
    return Array.isArray(rows) ? [...rows] : [];
  }
  const list = [...rows];
  list.sort((a, b) => {
    const aIn = String(a?.status ?? "").toLowerCase() === "inprogress";
    const bIn = String(b?.status ?? "").toLowerCase() === "inprogress";
    if (aIn === bIn) return 0;
    return aIn ? -1 : 1;
  });
  return list;
}

/** Default shape for a voucher series (create mode). */
export const getEmptyVoucherSeries = () => ({
  id: null,
  name: "",
  productIds: [],
  ticketProduct: "",
  price: "",
  validUntil: "",
  status: "active",
  vouchers: [],
});

/** Display / API string for voucher price — always 2 decimal places. */
export function formatPriceTwoDecimals(value) {
  const n =
    typeof value === "number"
      ? value
      : parseFloat(String(value ?? "").replace(",", "."));
  if (Number.isNaN(n)) return "";
  return (Math.round(n * 100) / 100).toFixed(2);
}

function getProductUnitPrice(p) {
  if (!p || typeof p !== "object") return 0;
  const raw = p.price ?? p.salePrice ?? 0;
  const n = parseFloat(raw);
  return Number.isNaN(n) ? 0 : n;
}

function hasStoredVoucherPrice(iv) {
  if (iv == null || iv.price === undefined || iv.price === null) return false;
  if (typeof iv.price === "number" && !Number.isNaN(iv.price))
    return iv.price >= 0;
  const s = String(iv.price).trim();
  if (s === "") return false;
  const n = parseFloat(s.replace(",", "."));
  return !Number.isNaN(n) && n >= 0;
}

/** Voucher code: 8 random digits (0–9). */
const VCH_CODE_LENGTH = 8;

function generateUniqueVchCode(codeSet) {
  let attempts = 0;
  while (attempts < 100000) {
    attempts += 1;
    let code = "";
    for (let i = 0; i < VCH_CODE_LENGTH; i += 1) {
      code += Math.floor(Math.random() * 10);
    }
    if (!codeSet.has(code)) {
      codeSet.add(code);
      return code;
    }
  }
  // Fallback: timestamp-based 8-digit number
  const fallback = String(Date.now()).slice(-VCH_CODE_LENGTH);
  codeSet.add(fallback);
  return fallback;
}

function appendGeneratedVchCodes(existing, requestedCount) {
  const existingList = existing || [];
  const existingLen = existingList.length;
  const remaining = Math.max(0, MAX_CODES_PER_SERIES - existingLen);
  const requested = Math.max(0, Math.floor(Number(requestedCount) || 0));
  const n = Math.min(remaining, requested);
  if (n <= 0) return existingList;

  const codeSet = new Set(existingList.map((v) => String(v.code)));
  const added = [];
  for (let i = 0; i < n; i += 1) {
    added.push({
      code: generateUniqueVchCode(codeSet),
      status: "ready",
    });
  }
  return [...existingList, ...added];
}

/** Kódy v stave `ready` (alebo legacy `active`) je možné zmazať; `done` nie. */
function isVoucherCodeDeletable(v) {
  const s = String(v?.status ?? "").toLowerCase();
  return s === "ready" || s === "active";
}

/** Len `ready` (alebo legacy `active`) — možnosť výberu a hromadnej tlače; `done` nie. */
function isVoucherCodeSelectable(v) {
  const s = String(v?.status ?? "").toLowerCase();
  return s === "ready" || s === "active" || s === "inprogress";
}

function validateBasic(values, t) {
  const e = {};
  if (!String(values.name || "").trim()) {
    e.name = t("validation.required", { defaultValue: "Required" });
  }
  if (!String(values.validUntil || "").trim()) {
    e.validUntil = t("validation.required", { defaultValue: "Required" });
  }
  const pids = Array.isArray(values.productIds) ? values.productIds : [];
  if (pids.length === 0) {
    e.productIds = t("validation.required", { defaultValue: "Required" });
  }
  const priceRaw = String(values.price ?? "").trim();
  if (!priceRaw) {
    e.price = t("validation.required", { defaultValue: "Required" });
  } else {
    const n = parseFloat(priceRaw.replace(",", "."));
    if (Number.isNaN(n)) {
      e.price = t("validation.numeric", { defaultValue: "Must be a number" });
    } else if (n < 0) {
      e.price = t("validation.nonNegative", {
        defaultValue: "Hodnota musí byť aspoň 0.",
      });
    }
  }
  return e;
}

/**
 * Shared voucher series form — tabs aligned with cart-rules RuleForm pattern.
 * @param {(values: object) => void} [onSave] — called with current form values when valid (e.g. future API).
 * @param {() => void} [onCancel] — navigate away / discard (e.g. back to list).
 */
const VoucherForm = ({ mode, initialValues = {}, onSave, onCancel }) => {
  const { t, i18n } = useTranslation();
  const { getAuthUser } = useJumboAuth();
  const authCustomer = getAuthUser()?.customer ?? null;
  const [activeTab, setActiveTab] = React.useState("basic");
  const [values, setValues] = React.useState(() => ({
    ...getEmptyVoucherSeries(),
    ...initialValues,
  }));
  const [generateCount, setGenerateCount] = React.useState(10);
  const [codesPage, setCodesPage] = React.useState(1);
  const [codesSearchQuery, setCodesSearchQuery] = React.useState("");
  const [generateFeedback, setGenerateFeedback] = React.useState(null);
  const [touched, setTouched] = React.useState({});
  const lastSavedVouchersRef = React.useRef(
    JSON.parse(
      JSON.stringify(
        Array.isArray(initialValues?.vouchers) ? initialValues.vouchers : [],
      ),
    ),
  );
  /** User edited price — do not overwrite from product on product change / async load. */
  const priceManuallyAdjustedRef = React.useRef(false);
  /** Detail load included a saved price — do not replace when ticket products finish loading. */
  const hadStoredPriceOnInitRef = React.useRef(false);
  const [ticketProducts, setTicketProducts] = React.useState([]);
  const [productsLoading, setProductsLoading] = React.useState(true);
  const [productsError, setProductsError] = React.useState(null);
  const [selectedCodes, setSelectedCodes] = React.useState([]);
  const [printModalOpen, setPrintModalOpen] = React.useState(false);

  const customer = getAuthUser()?.customer;
  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language || "sk";

  React.useEffect(() => {
    const merged = { ...getEmptyVoucherSeries(), ...initialValues };
    if (!Array.isArray(merged.productIds)) {
      merged.productIds = [];
    }
    if (
      merged.productIds.length === 0 &&
      merged.productId != null &&
      String(merged.productId).trim() !== ""
    ) {
      merged.productIds = [String(merged.productId).trim()];
    }
    if ("productId" in merged) {
      delete merged.productId;
    }
    setValues(merged);
    setTouched({});
    priceManuallyAdjustedRef.current = false;
    hadStoredPriceOnInitRef.current = hasStoredVoucherPrice(initialValues);
    lastSavedVouchersRef.current = JSON.parse(
      JSON.stringify(
        Array.isArray(initialValues?.vouchers) ? initialValues.vouchers : [],
      ),
    );
  }, [initialValues]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setProductsLoading(true);
        setProductsError(null);
        const raw = await getDSOKProducts();
        const list = normalizeDsokProductsList(raw);
        const tickets = list.filter((p) => p?.productType === "ticket");
        if (!cancelled) setTicketProducts(tickets);
      } catch (e) {
        if (!cancelled) setProductsError(e);
      } finally {
        if (!cancelled) setProductsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!ticketProducts.length) return;
    setValues((prev) => {
      const ids = Array.isArray(prev.productIds) ? prev.productIds : [];
      if (ids.length === 0) return prev;
      const labels = ids.map((id) => {
        const p = ticketProducts.find((x) => String(x._id) === String(id));
        return p ? resolveProductDisplayName(p, defaultLanguage) : String(id);
      });
      const joined = labels.join(", ");
      return prev.ticketProduct === joined
        ? prev
        : { ...prev, ticketProduct: joined };
    });
  }, [ticketProducts, defaultLanguage]);

  /** Prefill price from first selected product when products load and voucher has no stored price / no manual edit. */
  React.useEffect(() => {
    if (!ticketProducts.length) return;
    if (hadStoredPriceOnInitRef.current) return;
    if (priceManuallyAdjustedRef.current) return;
    setValues((prev) => {
      const ids = Array.isArray(prev.productIds) ? prev.productIds : [];
      const firstId = ids[0];
      if (!firstId) return prev;
      const p = ticketProducts.find((x) => String(x._id) === String(firstId));
      if (!p) return prev;
      const formatted = formatPriceTwoDecimals(getProductUnitPrice(p));
      if (prev.price === formatted) return prev;
      return { ...prev, price: formatted };
    });
  }, [ticketProducts]);

  const errors = React.useMemo(() => validateBasic(values, t), [values, t]);

  const setField = (field) => (e) => {
    const v = e?.target?.value ?? "";
    setValues((prev) => ({ ...prev, [field]: v }));
  };

  const handleTabChange = (_, v) => {
    if (v === "codes") {
      const err = validateBasic(values, t);
      if (Object.keys(err).length > 0) {
        setTouched({
          name: true,
          validUntil: true,
          productIds: true,
          price: true,
        });
        return;
      }
    }
    setActiveTab(v);
  };

  /** Stav riadku kódu v tabuľke kódov: `ready` | `done` (legacy `active` ako ready). */
  const resolveCodeStatusLabel = React.useCallback(
    (status) => {
      const s = String(status || "").toLowerCase();
      if (s === "ready" || s === "active") {
        return t(`${VOUCHERS_I18N}.codes.status.ready`, {
          defaultValue: "Pripravený",
        });
      }
      if (s === "done") {
        return t(`${VOUCHERS_I18N}.codes.status.done`, {
          defaultValue: "Hotovo",
        });
      }
      return status || "—";
    },
    [t],
  );

  const vouchers = React.useMemo(
    () => (Array.isArray(values.vouchers) ? values.vouchers : []),
    [values.vouchers],
  );

  const filteredVouchers = React.useMemo(
    () =>
      filterVoucherCodesBySearch(vouchers, codesSearchQuery, (row) =>
        resolveCodeStatusLabel(row.status),
      ),
    [vouchers, codesSearchQuery, resolveCodeStatusLabel],
  );

  const displayVouchers = React.useMemo(
    () => sortVoucherCodesInProgressFirst(filteredVouchers),
    [filteredVouchers],
  );

  const maxGeneratable = Math.max(0, MAX_CODES_PER_SERIES - vouchers.length);

  React.useEffect(() => {
    setCodesPage(1);
  }, [codesSearchQuery]);

  React.useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(displayVouchers.length / CODES_PAGE_SIZE),
    );
    setCodesPage((p) => Math.min(Math.max(1, p), totalPages));
  }, [displayVouchers.length]);

  React.useEffect(() => {
    if (maxGeneratable <= 0) return;
    setGenerateCount((c) => Math.min(Math.max(1, c), maxGeneratable));
  }, [maxGeneratable]);

  const handleTicketProductsChange = (e) => {
    const raw = e?.target?.value;
    const idList = Array.isArray(raw)
      ? raw.map((x) => String(x))
      : raw != null
        ? [String(raw)]
        : [];
    setValues((prev) => {
      let nextPrice = prev.price;
      if (!priceManuallyAdjustedRef.current) {
        if (idList.length === 0) {
          nextPrice = "";
        } else {
          const first = ticketProducts.find(
            (x) => String(x._id) === String(idList[0]),
          );
          nextPrice = first
            ? formatPriceTwoDecimals(getProductUnitPrice(first))
            : "";
        }
      }
      const labels = idList.map((id) => {
        const p = ticketProducts.find((x) => String(x._id) === String(id));
        return p ? resolveProductDisplayName(p, defaultLanguage) : String(id);
      });
      return {
        ...prev,
        productIds: idList,
        ticketProduct: labels.join(", "),
        price: nextPrice,
      };
    });
    setTouched((prev) => ({ ...prev, productIds: true }));
  };

  const handlePriceChange = (e) => {
    priceManuallyAdjustedRef.current = true;
    const raw = e?.target?.value ?? "";
    setValues((prev) => ({ ...prev, price: raw }));
  };

  const handlePriceBlur = () => {
    setTouched((prev) => ({ ...prev, price: true }));
    setValues((prev) => {
      const raw = String(prev.price ?? "").trim();
      if (raw === "") return prev;
      const n = parseFloat(raw.replace(",", "."));
      if (Number.isNaN(n)) return prev;
      const clamped = Math.max(0, n);
      return { ...prev, price: formatPriceTwoDecimals(clamped) };
    });
  };

  const customerCurrencyCode =
    customer?.dsokSettings?.customer?.currencyCode || "EUR";

  const ticketProductLabel = t(`${VOUCHERS_I18N}.form.fields.ticketProduct`, {
    defaultValue: "Ticket type",
  });

  const ticketProductMultiHint = t(
    `${VOUCHERS_I18N}.form.ticketProductMultiHint`,
    {
      defaultValue: "Vyberte jeden alebo viac typov lístkov.",
    },
  );

  const productIdsSafe = Array.isArray(values.productIds)
    ? values.productIds
    : [];
  const orphanProductIds = productIdsSafe.filter(
    (id) => !ticketProducts.some((p) => String(p._id) === String(id)),
  );
  const showOrphanTickets = orphanProductIds.length > 0;

  const handleGenerateCodes = () => {
    const list = Array.isArray(values.vouchers) ? values.vouchers : [];
    const remaining = Math.max(0, MAX_CODES_PER_SERIES - list.length);

    if (remaining <= 0) {
      setGenerateFeedback({
        severity: "error",
        message: t(`${VOUCHERS_I18N}.codes.generateMaxSeries`, {
          defaultValue: "Maximálny počet kódov pre jednu sériu je 200.",
        }),
      });
      return;
    }

    const rawRequested = Math.min(
      MAX_CODES_PER_SERIES,
      Math.max(1, Math.floor(Number(generateCount) || 1)),
    );
    const willAdd = Math.min(remaining, rawRequested);

    if (rawRequested > remaining) {
      setGenerateFeedback({
        severity: "warning",
        message: t(`${VOUCHERS_I18N}.codes.generateCappedToRemaining`, {
          count: willAdd,
          defaultValue:
            "Počet kódov je obmedzený na 200 na sériu. Bolo pridaných {{count}} kódov.",
        }),
      });
    } else {
      setGenerateFeedback(null);
    }

    setValues((prev) => {
      const prevList = Array.isArray(prev.vouchers) ? prev.vouchers : [];
      return {
        ...prev,
        vouchers: appendGeneratedVchCodes(prevList, rawRequested),
      };
    });
  };

  const handleDeleteCode = (codeToRemove) => {
    setValues((prev) => {
      const list = Array.isArray(prev.vouchers) ? prev.vouchers : [];
      const target = list.find((v) => String(v.code) === String(codeToRemove));
      if (!target || !isVoucherCodeDeletable(target)) {
        return prev;
      }
      return {
        ...prev,
        vouchers: list.filter((v) => String(v.code) !== String(codeToRemove)),
      };
    });
  };

  const deleteDisabledTooltip = t(
    `${VOUCHERS_I18N}.codes.deleteDisabledTooltip`,
    {
      defaultValue:
        "Kód v stave „hotovo“ nie je možné vymazať. Zmazať možno len kódy v stave pripravený.",
    },
  );

  const deleteCodeTooltip = t(`${VOUCHERS_I18N}.codes.deleteCodeTooltip`, {
    defaultValue: "Vymazať kód",
  });

  const paginatedVouchers = React.useMemo(() => {
    const start = (codesPage - 1) * CODES_PAGE_SIZE;
    return displayVouchers.slice(start, start + CODES_PAGE_SIZE);
  }, [displayVouchers, codesPage]);

  const codesPageCount = Math.max(
    1,
    Math.ceil(displayVouchers.length / CODES_PAGE_SIZE),
  );

  React.useEffect(() => {
    const list = Array.isArray(values.vouchers) ? values.vouchers : [];
    const allowed = new Set(
      list.filter((v) => isVoucherCodeSelectable(v)).map((v) => String(v.code)),
    );
    setSelectedCodes((prev) => prev.filter((c) => allowed.has(String(c))));
  }, [values.vouchers]);

  const selectableOnPage = React.useMemo(
    () => paginatedVouchers.filter((v) => isVoucherCodeSelectable(v)),
    [paginatedVouchers],
  );

  const selectableCodesOnPage = React.useMemo(
    () => selectableOnPage.map((v) => String(v.code)),
    [selectableOnPage],
  );

  const allSelectableOnPageSelected = React.useMemo(() => {
    if (selectableCodesOnPage.length === 0) return false;
    return selectableCodesOnPage.every((c) => selectedCodes.includes(c));
  }, [selectableCodesOnPage, selectedCodes]);

  const someSelectableOnPageSelected = React.useMemo(
    () => selectableCodesOnPage.some((c) => selectedCodes.includes(c)),
    [selectableCodesOnPage, selectedCodes],
  );

  const selectAllIndeterminate =
    someSelectableOnPageSelected && !allSelectableOnPageSelected;

  const handleToggleCodeRow = React.useCallback((code, selectable) => {
    if (!selectable) return;
    const s = String(code);
    setSelectedCodes((prev) => {
      const i = prev.indexOf(s);
      if (i >= 0) return prev.filter((c) => c !== s);
      return [...prev, s];
    });
  }, []);

  const handleSelectAllPage = React.useCallback(
    (event) => {
      const checked = Boolean(event.target.checked);
      setSelectedCodes((prev) => {
        if (checked) {
          const next = new Set(prev);
          selectableCodesOnPage.forEach((c) => next.add(c));
          return Array.from(next);
        }
        return prev.filter((c) => !selectableCodesOnPage.includes(c));
      });
    },
    [selectableCodesOnPage],
  );

  const selectedReadyCodesForPrint = React.useMemo(() => {
    const list = Array.isArray(values.vouchers) ? values.vouchers : [];
    const ready = new Set(
      list.filter((v) => isVoucherCodeSelectable(v)).map((v) => String(v.code)),
    );
    return selectedCodes.filter((c) => ready.has(String(c)));
  }, [selectedCodes, values.vouchers]);

  const submitVoucherForm = () => {
    const err = validateBasic(values, t);
    if (Object.keys(err).length > 0) {
      setTouched({
        name: true,
        validUntil: true,
        productIds: true,
        price: true,
      });
      setActiveTab("basic");
      return;
    }
    if (typeof onSave !== "function") return;
    const payload = values;
    Promise.resolve(onSave(payload))
      .then(() => {
        lastSavedVouchersRef.current = JSON.parse(
          JSON.stringify(
            Array.isArray(payload.vouchers) ? payload.vouchers : [],
          ),
        );
      })
      .catch(() => {});
  };

  const handleSaveClick = () => {
    submitVoucherForm();
  };

  const handleCodesCancelClick = () => {
    setValues((prev) => ({
      ...prev,
      vouchers: JSON.parse(JSON.stringify(lastSavedVouchersRef.current)),
    }));
    setCodesSearchQuery("");
    setCodesPage(1);
    setGenerateFeedback(null);
    setSelectedCodes([]);
  };

  const basicFields = (
    <Box sx={{ p: 3 }} component="div">
      <Stack spacing={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={6}>
            <Stack direction="row" spacing={3} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={values.status === "active"}
                    onChange={(e) =>
                      setValues((prev) => ({
                        ...prev,
                        status: e.target.checked ? "active" : "inactive",
                      }))
                    }
                  />
                }
                label={t(`${VOUCHERS_I18N}.form.fields.status`, {
                  defaultValue: "Status",
                })}
              />
            </Stack>
          </Grid>
          <Grid item xs={12} md={12} lg={12} />
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              fullWidth
              name="name"
              label={t(`${VOUCHERS_I18N}.form.fields.name`, {
                defaultValue: "Name",
              })}
              value={values.name}
              onChange={setField("name")}
              onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name ? errors.name : ""}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              fullWidth
              name="validUntil"
              label={t(`${VOUCHERS_I18N}.form.fields.validUntil`, {
                defaultValue: "Valid until",
              })}
              type="date"
              value={values.validUntil || ""}
              onChange={setField("validUntil")}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, validUntil: true }))
              }
              InputLabelProps={{ shrink: true }}
              error={Boolean(touched.validUntil && errors.validUntil)}
              helperText={touched.validUntil ? errors.validUntil : ""}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            {productsError ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {t(`${VOUCHERS_I18N}.form.productsLoadError`, {
                  defaultValue: "Products could not be loaded.",
                })}
              </Alert>
            ) : null}
            <Stack direction="row" spacing={1} alignItems="flex-start">
              {productsLoading ? (
                <CircularProgress size={24} sx={{ mt: 1 }} />
              ) : null}
              <FormControl
                fullWidth
                sx={{ flex: 1 }}
                error={Boolean(touched.productIds && errors.productIds)}
                disabled={productsLoading}
              >
                <InputLabel id="voucher-ticket-type-label">
                  {ticketProductLabel}
                </InputLabel>
                <Select
                  labelId="voucher-ticket-type-label"
                  multiple
                  value={productIdsSafe}
                  onChange={handleTicketProductsChange}
                  input={<OutlinedInput label={ticketProductLabel} />}
                  onBlur={() =>
                    setTouched((prev) => ({ ...prev, productIds: true }))
                  }
                  renderValue={(selected) => (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {(selected || []).map((id) => {
                        const p = ticketProducts.find(
                          (x) => String(x._id) === String(id),
                        );
                        const label = p
                          ? resolveProductDisplayName(p, defaultLanguage)
                          : String(id);
                        return (
                          <Chip key={String(id)} label={label} size="small" />
                        );
                      })}
                    </Stack>
                  )}
                >
                  {showOrphanTickets
                    ? orphanProductIds.map((id) => (
                        <MenuItem key={String(id)} value={id} disabled>
                          {String(id)}
                        </MenuItem>
                      ))
                    : null}
                  {ticketProducts.map((p) => (
                    <MenuItem key={String(p._id)} value={p._id}>
                      {resolveProductDisplayName(p, defaultLanguage)}
                    </MenuItem>
                  ))}
                </Select>
                {touched.productIds && errors.productIds ? (
                  <FormHelperText>{errors.productIds}</FormHelperText>
                ) : (
                  <FormHelperText>{ticketProductMultiHint}</FormHelperText>
                )}
              </FormControl>
            </Stack>
          </Grid>
          <Grid item xs={6} md={3} lg={2}>
            <TextField
              fullWidth
              name="price"
              label={t(`${VOUCHERS_I18N}.form.fields.price`, {
                defaultValue: "Cena",
              })}
              value={values.price ?? ""}
              onChange={handlePriceChange}
              onBlur={handlePriceBlur}
              inputProps={{ inputMode: "decimal" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {`\u00A0${customerCurrencyCode}`}
                  </InputAdornment>
                ),
              }}
              error={Boolean(touched.price && errors.price)}
              helperText={touched.price ? errors.price : ""}
            />
          </Grid>
        </Grid>
        {typeof onCancel === "function" || typeof onSave === "function" ? (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {typeof onCancel === "function" ? (
              <Button variant="outlined" color="inherit" onClick={onCancel}>
                {t("buttons.cancel", { defaultValue: "Cancel" })}
              </Button>
            ) : null}
            {typeof onSave === "function" ? (
              <Button variant="contained" onClick={handleSaveClick}>
                {t("buttons.save", { defaultValue: "Save" })}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );

  const codesSearchPlaceholder = t(`${VOUCHERS_I18N}.codes.searchPlaceholder`, {
    defaultValue: "Vyhľadať kód alebo stav",
  });

  const codesPanel = (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "flex-end" }}
          flexWrap="wrap"
        >
          <TextField
            type="number"
            size="small"
            label={t(`${VOUCHERS_I18N}.form.generateCountLabel`, {
              defaultValue: "Number of codes to generate",
            })}
            value={maxGeneratable <= 0 ? 1 : generateCount}
            disabled={maxGeneratable <= 0}
            onChange={(e) => {
              const raw = parseInt(e.target.value, 10);
              const maxG = Math.max(0, MAX_CODES_PER_SERIES - vouchers.length);
              if (maxG <= 0) return;
              setGenerateCount(
                Math.min(maxG, Math.max(1, Number.isNaN(raw) ? 1 : raw)),
              );
              setGenerateFeedback(null);
            }}
            inputProps={{
              min: 1,
              max: Math.max(1, maxGeneratable),
            }}
            sx={{ minWidth: { sm: 110 } }}
          />
          <Button
            variant="contained"
            onClick={handleGenerateCodes}
            disabled={maxGeneratable <= 0}
          >
            {t(`${VOUCHERS_I18N}.form.generateCodes`, {
              defaultValue: "Generate codes",
            })}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
            disabled={selectedReadyCodesForPrint.length === 0}
            onClick={() => setPrintModalOpen(true)}
          >
            {t(`${VOUCHERS_I18N}.codes.print.bulkButton`, {
              defaultValue: "Tlač voucherov",
            })}
          </Button>
        </Stack>

        {generateFeedback ? (
          <Alert severity={generateFeedback.severity}>
            {generateFeedback.message}
          </Alert>
        ) : null}

        {vouchers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {mode === "create"
              ? t(`${VOUCHERS_I18N}.form.noCodesCreate`, {
                  defaultValue:
                    "No codes yet. Codes will appear here after generation.",
                })
              : t(`${VOUCHERS_I18N}.form.noCodes`, {
                  defaultValue: "No voucher codes.",
                })}
          </Typography>
        ) : (
          <>
            <Stack
              direction="row"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              spacing={1}
            >
              <Box
                width={{ xs: "100%", sm: "40%" }}
                display="flex"
                flexDirection="row"
              >
                <Card sx={{ mb: 0, width: "100%", flex: 5 }}>
                  <JumboSearch
                    page={VOUCHER_CODES_SEARCH_PAGE}
                    value={codesSearchQuery}
                    onChange={setCodesSearchQuery}
                    sx={{ width: "100%" }}
                    placeholder={codesSearchPlaceholder}
                  />
                </Card>
              </Box>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ flex: 1, justifyContent: "center" }}
              />
              <Pagination
                count={codesPageCount}
                color="primary"
                page={codesPage}
                onChange={(_, val) => setCodesPage(val)}
              />
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 48 }}>
                      <Checkbox
                        size="small"
                        indeterminate={selectAllIndeterminate}
                        checked={
                          selectableCodesOnPage.length > 0 &&
                          allSelectableOnPageSelected
                        }
                        disabled={selectableCodesOnPage.length === 0}
                        onChange={handleSelectAllPage}
                        inputProps={{
                          "aria-label": t(`${VOUCHERS_I18N}.codes.selectAll`, {
                            defaultValue: "Vybrať všetky na stránke",
                          }),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {t(`${VOUCHERS_I18N}.codes.columns.code`, {
                        defaultValue: "Voucher code",
                      })}
                    </TableCell>
                    <TableCell>
                      {t(`${VOUCHERS_I18N}.codes.columns.status`, {
                        defaultValue: "Status",
                      })}
                    </TableCell>
                    <TableCell align="right">
                      {t(`${VOUCHERS_I18N}.codes.columns.actions`, {
                        defaultValue: "Akcie",
                      })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVouchers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2" color="text.secondary">
                          {t(`${VOUCHERS_I18N}.codes.searchNoResults`, {
                            defaultValue:
                              "Žiadne kódy nevyhovujú vyhľadávaniu.",
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedVouchers.map((v) => {
                      const canDelete = isVoucherCodeDeletable(v);
                      const canSelect = isVoucherCodeSelectable(v);
                      const codeStr = String(v.code);
                      const isChecked = selectedCodes.includes(codeStr);
                      return (
                        <TableRow key={String(v.code)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={canSelect && isChecked}
                              disabled={!canSelect}
                              onChange={() =>
                                handleToggleCodeRow(v.code, canSelect)
                              }
                              inputProps={{
                                "aria-label": `${t(`${VOUCHERS_I18N}.codes.columns.select`, { defaultValue: "Vybrať" })} ${codeStr}`,
                              }}
                            />
                          </TableCell>
                          <TableCell>{v.code}</TableCell>
                          <TableCell>
                            {resolveCodeStatusLabel(v.status)}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                canDelete
                                  ? deleteCodeTooltip
                                  : deleteDisabledTooltip
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={!canDelete}
                                  onClick={() => handleDeleteCode(v.code)}
                                  aria-label={
                                    canDelete
                                      ? deleteCodeTooltip
                                      : deleteDisabledTooltip
                                  }
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {typeof onCancel === "function" || typeof onSave === "function" ? (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {typeof onCancel === "function" ? (
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCodesCancelClick}
              >
                {t("buttons.cancel", { defaultValue: "Cancel" })}
              </Button>
            ) : null}
            {typeof onSave === "function" ? (
              <Button variant="contained" onClick={submitVoucherForm}>
                {t("buttons.save", { defaultValue: "Save" })}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <VoucherCodesPrintModal
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        customer={authCustomer}
        codes={selectedReadyCodesForPrint}
        voucherId={values.id}
      />
      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleTabChange}>
            <Tab
              label={t(`${VOUCHERS_I18N}.tabs.basic`, {
                defaultValue: "Basic information",
              })}
              value="basic"
            />
            <Tab
              label={t(`${VOUCHERS_I18N}.tabs.codes`, {
                defaultValue: "Codes",
              })}
              value="codes"
            />
          </TabList>
        </Box>
        <TabPanel value="basic" sx={{ p: 0 }}>
          {basicFields}
        </TabPanel>
        <TabPanel value="codes" sx={{ p: 0 }}>
          {codesPanel}
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default VoucherForm;
